import { type ReactNode } from 'react';
import { tv } from 'tailwind-variants';
import { panelLead, panelTitle, screenToolbarRow } from './panelHeader.styles.js';

const root = tv({
  base: 'flex min-w-0 max-w-full flex-col gap-1',
  variants: {
    layout: {
      stack: '',
      toolbar: screenToolbarRow(),
    },
  },
  defaultVariants: {
    layout: 'stack',
  },
});

export interface PageSectionHeaderProps {
  /** Omit when the shell `EnterprisePageHeader` already shows the page title. */
  readonly title?: string;
  readonly lead?: string;
  readonly actions?: ReactNode;
  readonly className?: string;
  readonly titleId?: string;
  readonly layout?: 'stack' | 'toolbar';
}

/**
 * Canonical in-content section header below the enterprise shell.
 * Use for panel/table titles — one `<h1>` per route lives in `EnterprisePageHeader`.
 */
export function PageSectionHeader({
  title,
  lead,
  actions,
  className,
  titleId,
  layout = 'stack',
}: PageSectionHeaderProps): JSX.Element | null {
  if (title === undefined && lead === undefined && actions === undefined) {
    return null;
  }

  const hasCopy = title !== undefined || lead !== undefined;
  const titleBlock = hasCopy ? (
    <div className="min-w-0 flex-1">
      {title !== undefined ? (
        <h2 id={titleId} className={panelTitle()}>
          {title}
        </h2>
      ) : null}
      {lead !== undefined ? (
        <p
          className={panelLead({
            // G7: Spec D14 reading track — not freestyle max-w-3xl / page-local invent.
            className:
              title !== undefined
                ? 'mt-1 max-w-[var(--container-reading)]'
                : 'max-w-[var(--container-reading)]',
          })}
        >
          {lead}
        </p>
      ) : null}
    </div>
  ) : null;

  if (layout === 'toolbar') {
    return (
      <header className={root({ layout, className })} data-testid="page-section-header">
        {titleBlock}
        {actions !== undefined ? (
          <div
            className="flex min-w-0 w-full max-w-full flex-wrap items-center gap-2 md:w-auto md:shrink-0 [&_button]:max-w-full [&_button]:whitespace-normal"
            data-testid="page-section-header-actions"
          >
            {actions}
          </div>
        ) : null}
      </header>
    );
  }

  return (
    <header className={root({ layout, className })} data-testid="page-section-header">
      {titleBlock}
      {actions}
    </header>
  );
}
